// ============================================================
// Leave Service
// ============================================================
// Integrates real Backend API for Leave Management with fallback

import { leaveRequestsApi } from '@/api/leaveRequests.api';
import { leaveTypesApi } from '@/api/leaveTypes.api';
import { mockLeaveRequests } from '@/mock/leaveRequests';
import useSchoolStore from '@/store/schoolStore';

function extractItems(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data?.items)) return data.data.items;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

function getTime(dateStr) {
  if (!dateStr) return 0;
  const t = new Date(dateStr).getTime();
  return isNaN(t) ? 0 : t;
}

export const leaveService = {
  // Get pending leave requests (for Principal & Org Admin)
  getPendingLeaves: async (params = {}) => {
    const activeSchoolId = params.school_id || params.schoolId || useSchoolStore.getState().currentSchool?.id;
    if (!activeSchoolId) {
      return [];
    }
    const res = await leaveRequestsApi.getPending({ ...params, school_id: activeSchoolId });
    const rawItems = extractItems(res);
    const items = rawItems.map((item) => ({
      id: item.leave_request_id || item.id || item.leaveRequestId,
      leaveRequestId: item.leave_request_id || item.id || item.leaveRequestId,
      staffId: item.staff_id || item.staffId,
      employeeId: item.employee_id || item.employeeId || '',
      staffName: item.staff_name || item.staffName || item.teacherName || 'Staff Member',
      teacherName: item.staff_name || item.staffName || item.teacherName || 'Staff Member',
      leaveType: item.leave_type || item.leaveType,
      fromDate: item.from_date || item.fromDate,
      toDate: item.to_date || item.toDate,
      numberOfDays: item.number_of_days ?? item.numberOfDays ?? 1,
      reason: item.reason || '',
      status: (item.leave_status || item.status || 'PENDING').toUpperCase(),
      appliedOn: item.applied_on || item.appliedOn || item.appliedDate || null,
      approvedOn: item.approved_on || item.approvedOn || null,
      rejectedOn: item.rejected_on || item.rejectedOn || null,
      updatedAt: item.updated_at || item.updatedAt || null,
      remarks: item.remarks || item.approval_remarks || item.rejection_remarks || '',
      rejectionReason: item.rejection_reason || item.rejectionReason || '',
    }));

    return items.sort((a, b) => getTime(b.appliedOn) - getTime(a.appliedOn));
  },

  // Alias for legacy calls
  getAllLeaves: async (filters = {}) => {
    return await leaveService.getPendingLeaves(filters);
  },

  // Summary calculation for dashboard widgets
  getLeaveSummary: async (role, userId = null, schoolId = null) => {
    try {
      if (role === 'teacher') {
        const my = await leaveService.getMyLeaves();
        const pending = my.filter((r) => r.status === 'PENDING').length;
        const approved = my.filter((r) => r.status === 'APPROVED').length;
        const rejected = my.filter((r) => r.status === 'REJECTED').length;
        return { pending, approved, rejected, balance: 14 - approved };
      }
      const activeSchoolId = schoolId || useSchoolStore.getState().currentSchool?.id;
      if (!activeSchoolId) {
        return { pending: 0, approvedToday: 0, rejectedToday: 0, recentRequests: 0 };
      }
      const pendingLeaves = await leaveService.getPendingLeaves({ school_id: activeSchoolId });
      return {
        pending: pendingLeaves.length,
        approvedToday: 0,
        rejectedToday: 0,
        recentRequests: pendingLeaves.length,
      };
    } catch (error) {
      return { pending: 0, approvedToday: 0, rejectedToday: 0, recentRequests: 0 };
    }
  },

  // Get logged-in staff member's leave requests
  getMyLeaves: async (params = {}) => {
    const res = await leaveRequestsApi.getMyLeaves(params);
    const rawItems = extractItems(res);
    const items = rawItems.map((item) => ({
      id: item.leave_request_id || item.id || item.leaveRequestId,
      leaveRequestId: item.leave_request_id || item.id || item.leaveRequestId,
      leaveType: item.leave_type || item.leaveType,
      leaveCode: item.leave_code || item.leaveCode,
      fromDate: item.from_date || item.fromDate,
      toDate: item.to_date || item.toDate,
      numberOfDays: item.number_of_days ?? item.numberOfDays ?? 1,
      status: (item.leave_status || item.status || 'PENDING').toUpperCase(),
      reason: item.reason || '',
      isHalfDay: item.is_half_day ?? item.isHalfDay ?? false,
      halfDaySession: item.half_day_session || item.halfDaySession,
      appliedOn: item.applied_on || item.appliedOn || item.appliedDate || null,
    }));

    return items.sort((a, b) => getTime(b.appliedOn) - getTime(a.appliedOn));
  },

  // Get detail of a specific leave request with timeline
  getMyLeaveDetail: async (id) => {
    try {
      const res = await leaveRequestsApi.getMyLeaveById(id);
      return res.data || res;
    } catch (error) {
      console.error('Error fetching leave detail:', error);
      throw error;
    }
  },

  // Apply for leave
  applyLeave: async (payload) => {
    try {
      const res = await leaveRequestsApi.apply(payload);
      return res.data || res;
    } catch (error) {
      console.error('Error applying for leave:', error);
      throw error;
    }
  },

  // Approve a leave request
  approveLeave: async (leaveId, remarks = '') => {
    try {
      const res = await leaveRequestsApi.approve(leaveId, { remarks });
      return res;
    } catch (error) {
      console.error('Error approving leave request:', error);
      throw error;
    }
  },

  // Reject a leave request
  rejectLeave: async (leaveId, remarks = '', rejectionReason = '') => {
    try {
      const res = await leaveRequestsApi.reject(leaveId, {
        rejection_reason: rejectionReason || remarks || 'Rejected by Admin',
        remarks: remarks || 'Rejected by Admin',
      });
      return res;
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      throw error;
    }
  },

  // Cancel my pending leave request
  cancelLeave: async (leaveId, remarks = '') => {
    try {
      const res = await leaveRequestsApi.cancelMyLeave(leaveId, remarks);
      return res;
    } catch (error) {
      console.error('Error cancelling leave request:', error);
      throw error;
    }
  },

  // Get my leave balances
  getMyBalance: async (year = new Date().getFullYear()) => {
    try {
      const res = await leaveRequestsApi.getMyBalance(year);
      if (res && res.items) {
        return res.items.map((item) => ({
          leaveTypeId: item.leave_type_id,
          leaveType: item.leave_type,
          code: item.code,
          allocated: item.allocated || 0,
          used: item.used || 0,
          remaining: item.remaining || 0,
          carryForward: item.carry_forward || 0,
        }));
      }
      return [];
    } catch (error) {
      console.warn('Backend leave balance API error:', error);
      return [];
    }
  },

  // Get active leave types for current school
  getLeaveTypes: async (schoolId) => {
    try {
      const res = await leaveTypesApi.getAll(schoolId);
      if (res && res.items) {
        return res.items.filter((item) => item.status === 'ACTIVE' || item.is_active);
      }
      return Array.isArray(res) ? res : [];
    } catch (error) {
      console.warn('Error fetching leave types:', error);
      return [];
    }
  },

  // Initialize leave balances for year
  initializeBalance: async (year = new Date().getFullYear()) => {
    return await leaveRequestsApi.initializeBalance(Number(year));
  },
};
